import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants/routes';
import logo from '../assets/logo/logo.svg';
import OAuthButtons from './OAuthButtons';
import TwoFactorVerification from './TwoFactorVerification';
import OnboardingFlow from './OnboardingFlow';
import ForceLightMode from './ForceLightMode';

export const AuthForm = ({ isLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { login, register, oauthLogin, complete2FALogin } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [usernameCheckLoading, setUsernameCheckLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const location = useLocation();
  const toastShown = useRef(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneDigits = formData.phone.replace('+92', '');
  
  // Enhanced validation with detailed feedback
  const validationStatus = {
    username: {
      isValid: formData.username && usernameAvailable && !usernameError,
      message: !formData.username ? 'Username is required' : 
               usernameError ? usernameError : 
               !usernameAvailable ? 'Username is already taken' : 
               'Username is available'
    },
    fullName: {
      isValid: formData.full_name && formData.full_name.trim().length > 0,
      message: !formData.full_name ? 'Full name is required' : 'Full name is valid'
    },
    email: {
      isValid: formData.email && emailRegex.test(formData.email) && !emailError,
      message: !formData.email ? 'Email is required' : 
               !emailRegex.test(formData.email) ? 'Please enter a valid email address' : 
               emailError ? emailError : 'Email is valid'
    },
    phone: {
      isValid: phoneDigits.length === 10 && !phoneError,
      message: !formData.phone ? 'Phone number is required' : 
               phoneDigits.length !== 10 ? 'Phone number must be exactly 10 digits after +92' : 
               phoneError ? phoneError : 'Phone number is valid'
    },
    password: {
      isValid: formData.password && formData.password.length >= 12 && !passwordError,
      message: !formData.password ? 'Password is required' : 
               formData.password.length < 12 ? 'Password must be at least 12 characters' : 
               passwordError ? passwordError : 'Password meets requirements'
    }
  };

  const isSignupValid = !isLogin && Object.values(validationStatus).every(field => field.isValid);

  React.useEffect(() => {
    if (!formData.username || isLogin) return;
    setUsernameCheckLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const axios = (await import('axios')).default;
        const res = await axios.get(`/api/users/check-username?username=${formData.username}`);
        setUsernameAvailable(res.data.available);
        setUsernameError(res.data.available ? '' : 'Username is already taken.');
      } catch {
        setUsernameAvailable(false);
        setUsernameError('Error checking username.');
      } finally {
        setUsernameCheckLoading(false);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [formData.username, isLogin]);

  React.useEffect(() => {
    if (isLogin && location.search.includes('verified=1') && !toastShown.current) {
      toast.success('Email verified successfully! You can now log in.');
      toastShown.current = true;
    }
  }, [isLogin, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (isLogin) {
      const result = await login({ username: formData.username, password: formData.password });
      setLoading(false);
      if (result === 'pending_approval') {
        navigate(ROUTES.PENDING_APPROVAL);
      } else if (result === 'requires_2fa') {
        setRequires2FA(true);
      } else if (result === 'suggest_2fa') {
        setShowOnboarding(true);
      } else if (result) {
        navigate(ROUTES.DASHBOARD);
      }
    } else {
      // Validate before attempting registration
      const phoneDigits = formData.phone.replace('+92', '');
      if (phoneDigits.length !== 10) {
        setPhoneError('Phone number must be exactly 10 digits after +92.');
        setLoading(false);
        return;
      }
      setPhoneError('');

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email || !emailRegex.test(formData.email)) {
        setEmailError('Please enter a valid email address.');
        setLoading(false);
        return;
      }
      setEmailError('');

      if (!formData.password || formData.password.length < 12) {
        setPasswordError('Password must be at least 12 characters long.');
        setLoading(false);
        return;
      }
      setPasswordError('');

      if (!usernameAvailable) {
        setUsernameError('Username is already taken.');
        setLoading(false);
        return;
      }
      setUsernameError('');

      setLoading(true);
      try {
        const response = await register(formData);
        if (response && response.detail) {
          // Registration successful - redirect to check email page
          navigate(ROUTES.CHECK_EMAIL);
        } else if (response === false) {
          // Registration failed (error already handled by register function)
        }
      } catch (error) {
        console.error('❌ Unexpected registration error:', error);
        toast.error('An unexpected error occurred during registration');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOAuthLogin = async (result) => {
    // OAuth login already handled in OAuthButtons component
    // Just navigate to dashboard since login was successful
    navigate(ROUTES.DASHBOARD);
  };

  const handle2FAVerification = async (code) => {
    setLoading(true);
    try {
      const result = await complete2FALogin(formData.username, code);
      setLoading(false);
      if (result) {
        // Add a small delay to ensure state is updated
        setTimeout(() => {
          // Use window.location.href to force a full page reload
          window.location.href = ROUTES.DASHBOARD;
        }, 100);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setFormData({ ...formData, password: '' });
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Use window.location.href to force a full page reload and ensure state is updated
    window.location.href = ROUTES.DASHBOARD;
  };

  function generateStrongPassword(length = 12) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  // Show 2FA verification screen if required (always light — ignore app dark theme)
  if (requires2FA) {
    return (
      <ForceLightMode>
        <TwoFactorVerification
          username={formData.username}
          onSuccess={handle2FAVerification}
          onBack={handleBackToLogin}
          onCancel={handleBackToLogin}
        />
      </ForceLightMode>
    );
  }

  // Show onboarding flow for first-time users (always light mode)
  if (showOnboarding) {
    return (
      <ForceLightMode>
        <OnboardingFlow
          username={formData.username}
          onComplete={handleOnboardingComplete}
        />
      </ForceLightMode>
    );
  }

  return (
    <div className="auth-page min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <img
            src={logo}
            alt="Tick Sync Logo"
            className="h-40 w-auto object-contain mb-6"
            style={{ background: 'transparent' }}
          />
          <h2 className="text-center text-3xl font-bold text-secondary-900">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-secondary-700">
                {isLogin ? 'Username or Email' : 'Username'}
              </label>
              <input
                type="text"
                required
                className={`input-field mt-1 !bg-white !border-gray-300 !text-gray-900 ${validationStatus.username.isValid ? 'border-green-500' : formData.username ? 'border-red-500' : ''}`}
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
              {usernameCheckLoading && !isLogin && <div className="text-xs text-gray-500">Checking username...</div>}
              {!usernameCheckLoading && !isLogin && (
                <div className={`text-xs mt-1 ${validationStatus.username.isValid ? 'text-green-600' : 'text-red-600'}`}>
                  {validationStatus.username.message}
                </div>
              )}
            </div>
            {!isLogin && (
              <>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Full Name</label>
                  <input
                    type="text"
                    required
                    className={`input-field mt-1 !bg-white !border-gray-300 !text-gray-900 ${validationStatus.fullName.isValid ? 'border-green-500' : formData.full_name ? 'border-red-500' : ''}`}
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  />
                  {!isLogin && (
                    <div className={`text-xs mt-1 ${validationStatus.fullName.isValid ? 'text-green-600' : 'text-red-600'}`}>
                      {validationStatus.fullName.message}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Email</label>
                  <input
                    type="email"
                    required
                    className={`input-field mt-1 !bg-white !border-gray-300 !text-gray-900 ${validationStatus.email.isValid ? 'border-green-500' : formData.email ? 'border-red-500' : ''}`}
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (!emailRegex.test(e.target.value)) {
                        setEmailError('Please enter a valid email address.');
                      } else {
                        setEmailError('');
                      }
                    }}
                  />
                  {!isLogin && (
                    <div className={`text-xs mt-1 ${validationStatus.email.isValid ? 'text-green-600' : 'text-red-600'}`}>
                      {validationStatus.email.message}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Phone</label>
                  <input
                    type="tel"
                    required
                    className={`input-field mt-1 !bg-white !border-gray-300 !text-gray-900 ${validationStatus.phone.isValid ? 'border-green-500' : formData.phone ? 'border-red-500' : ''}`}
                    value={formData.phone}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (!value.startsWith('+92')) value = '+92' + value.replace(/[^\d]/g, '');
                      let digits = value.slice(3).replace(/\D/g, '');
                      if (digits.length > 10) digits = digits.slice(0, 10);
                      setFormData({ ...formData, phone: '+92' + digits });
                    }}
                    placeholder="+923XXXXXXXXX"
                  />
                  {!isLogin && (
                    <div className={`text-xs mt-1 ${validationStatus.phone.isValid ? 'text-green-600' : 'text-red-600'}`}>
                      {validationStatus.phone.message}
                    </div>
                  )}
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium text-secondary-700 flex items-center">
                Password
                {!isLogin && (
                  <div className="ml-1 group relative">
                    <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-64 max-w-[calc(100vw-2rem)] whitespace-normal">
                      <div className="font-medium mb-1">Password Requirements:</div>
                      <div>• Minimum 12 characters</div>
                      <div>• At least 1 uppercase letter (A-Z)</div>
                      <div>• At least 1 lowercase letter (a-z)</div>
                      <div>• At least 1 number (0-9)</div>
                      <div>• At least 1 special character (!@#$%^&*)</div>
                      <div>• No common patterns (123, abc, password)</div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                )}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  className={`input-field mt-1 pr-12 !bg-white !border-gray-300 !text-gray-900 min-h-[44px] ${validationStatus.password.isValid ? 'border-green-500' : formData.password ? 'border-red-500' : ''}`}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center justify-center px-3 min-w-[44px] text-secondary-500"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {!isLogin && (
                <button
                  type="button"
                  className="mt-2 text-primary-600 hover:text-primary-500 text-sm font-medium min-h-[44px] sm:min-h-0"
                  onClick={() => setFormData({ ...formData, password: generateStrongPassword() })}
                >
                  Suggest password
                </button>
              )}
              {isLogin && (
                <div className="text-right mt-1">
                  <button
                    type="button"
                    className="text-primary-600 hover:text-primary-500 text-sm"
                    onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              {!isLogin && (
                <div className={`text-xs mt-1 ${validationStatus.password.isValid ? 'text-green-600' : 'text-red-600'}`}>
                  {validationStatus.password.message}
                </div>
              )}
              {!isLogin && !passwordError && formData.password && (
                <div className="text-xs mt-1 text-gray-600">
                  Password must be at least 12 characters with uppercase, lowercase, numbers, and special characters.
                </div>
              )}
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading || (isLogin ? false : !isSignupValid)}
              className={`group relative w-full btn-primary ${!isSignupValid && !isLogin ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign in' : 'Sign up')}
            </button>
          </div>
          <div className="text-center">
            <button
              type="button"
              className="text-primary-600 hover:text-primary-500"
              onClick={() => navigate(isLogin ? ROUTES.REGISTER : ROUTES.LOGIN)}
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>

          {/* OAuth Buttons - only show on login */}
          {isLogin && (
            <OAuthButtons onLoginSuccess={handleOAuthLogin} />
          )}
        </form>
      </div>
    </div>
  );
};

export default AuthForm;
